/**
 * Skills v2 status rules (docs/systems/SKILLS_V2.md section 4) that sit on top of the World's own status tick:
 *
 * - มึนงง (`stun`): the World's stun timer (`stunT`) is mirrored as the `stun` status so it can be shown, and a
 *   `stun` status added by a move sets the timer. A single hit of at least `breaksOnHeavy` of max HP ends a stun
 *   that was already running. After a stun ends the bearer is immune to stuns for `STATUS_RULES.stunImmune` s.
 *   Between two people (players and rivals) repeated stuns are diminished: within `drWindow` s of the first, the
 *   next lasts ×0.5, the third ×0.25, then none until the window ends.
 * - เลือดไหล (`bleed`): `moveDot` per metre moved, ×2 in qinggong (in the air, sprinting or dashing). It never
 *   kills by itself; the plain bleed tick can. Standing still for `stillAfter` s, the plain tick runs at `stillMul`.
 * - พิษ (`poison`): a stack added at max stacks bursts for `burstAtMax` of max HP (never lethal).
 * - Diminishing returns between people for every `drCategory` (stun, slow, silence): each group counts on its own.
 * - Bosses take stuns ×`bossStunMul` and shrug off any after `bossStunCap` in `drWindow` s.
 *
 * Deterministic: no random draws, time only through `step(dt)`.
 */
import { STATUSES } from '../status';
import type { Entity, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

export const STATUS_RULES = {
  /** Seconds of stun immunity after a stun ends. */
  stunImmune: 2,
  /** PvP diminishing returns: the window, and the factor for the 2nd, 3rd and later stuns. */
  drWindow: 15,
  drFactors: [1, 0.5, 0.25, 0] as readonly number[],
  /** Qinggong multiplies the bleed's per-metre damage by this. */
  bleedQinggongMul: 2,
  /** Seconds standing still before `stillMul` applies. */
  stillAfter: 1,
  /** Bosses: stuns × this, and none after this many in the window. */
  bossStunMul: 0.5,
  bossStunCap: 3,
} as const;

const STUN = STATUSES['stun']!;
const BLEED = STATUSES['bleed']!;
const person = (e: Entity) => e.kind === 'player' || e.kind === 'rival';
const isBoss = (e: Entity) => e.kind === 'monster' && /boss/i.test(e.brain?.defId ?? '');

export class StatusRulesSystem implements WorldSystem {
  readonly name = 'statusRules';
  private now = 0;
  /** Entity id → the stun timer at the end of the last step (to tell a running stun from a new one). */
  private readonly prevStun = new Map<number, number>();
  /** Entity id → time until which stuns do not take. */
  private readonly immuneUntil = new Map<number, number>();
  /** `entity id:category` → controls taken in the current window (PvP, and bosses' stuns). */
  private readonly dr = new Map<string, { start: number; n: number }>();
  /** `entity id:status` → stacks last seen (poison's burst). */
  private readonly stacks = new Map<string, number>();
  /** Entity id → seconds stood still while bleeding. */
  private readonly stillT = new Map<number, number>();
  /** Entity id → the last position seen while bleeding. */
  private readonly lastPos = new Map<number, { x: number; y: number; z: number }>();

  constructor(private readonly h: SystemHost) {}

  step(dt: number): void {
    this.now += dt;
    for (const e of this.h.world.entities.values()) {
      if (e.dead) {
        this.prevStun.delete(e.id);
        this.lastPos.delete(e.id);
        continue;
      }
      this.stun(e);
      this.bleed(e, dt);
    }
  }

  private stun(e: Entity): void {
    const st = e.statuses.find((s) => s.id === STUN.id);
    // A move that adds the status stuns for its time.
    if (st && st.t > e.stunT + 1e-9 && !this.prevStun.get(e.id)) e.stunT = st.t;
    // Immunity against stuns set outside a hit (a trap, a status); monsters' staged beats (systems/worldBosses.ts) are
    // their own business, so it only guards people and beasts here; hits are checked in observe.
    if (e.stunT > 0 && e.kind !== 'monster' && !this.prevStun.get(e.id) && (this.immuneUntil.get(e.id) ?? -1) > this.now)
      e.stunT = 0;
    if (e.stunT > 0) {
      e.blocking = false;
      if (st) st.t = e.stunT;
      else {
        e.statuses.push({ id: STUN.id, stacks: 1, t: e.stunT });
        this.h.emit({ type: 'status', id: e.id, status: STUN.id, stacks: 1 });
      }
      this.prevStun.set(e.id, e.stunT);
      return;
    }
    if (this.prevStun.get(e.id)) this.immuneUntil.set(e.id, this.now + STATUS_RULES.stunImmune);
    this.prevStun.delete(e.id);
    if (st) {
      e.statuses = e.statuses.filter((s) => s !== st);
      this.h.emit({ type: 'status', id: e.id, status: STUN.id, stacks: 0 });
    }
  }

  private bleed(e: Entity, dt: number): void {
    const st = e.statuses.find((s) => s.id === BLEED.id);
    if (!st) {
      this.lastPos.delete(e.id);
      this.stillT.delete(e.id);
      return;
    }
    const last = this.lastPos.get(e.id);
    this.lastPos.set(e.id, { x: e.x, y: e.y, z: e.z });
    if (!last) return;
    const d = Math.hypot(e.x - last.x, e.y - last.y, e.z - last.z);
    if (d <= 1e-6) {
      // Standing still: the plain tick (world.ts) runs at stillMul after a second; give back the rest.
      const still = (this.stillT.get(e.id) ?? 0) + dt;
      this.stillT.set(e.id, still);
      if (still >= STATUS_RULES.stillAfter && BLEED.stillMul !== undefined && BLEED.dotPct)
        e.hp = Math.min(e.maxHp, e.hp + e.maxHp * BLEED.dotPct * st.stacks * dt * (1 - BLEED.stillMul));
      return;
    }
    this.stillT.set(e.id, 0);
    if (!BLEED.moveDot || d > 5) return; // a teleport is not a walk
    const qinggong = e.z > 0 || e.sprinting || e.dashT > 0;
    const dmg = e.maxHp * BLEED.moveDot * st.stacks * d * (qinggong ? STATUS_RULES.bleedQinggongMul : 1);
    e.hp = Math.max(1, e.hp - dmg);
  }

  /** The window's factor for one more control of `cat` on `id` (counting it), from `factors`. */
  private diminish(id: number, cat: string, factors: readonly number[]): number {
    const key = `${id}:${cat}`;
    let r = this.dr.get(key);
    if (!r || this.now - r.start >= STATUS_RULES.drWindow) this.dr.set(key, (r = { start: this.now, n: 0 }));
    const f = factors[Math.min(r.n, factors.length - 1)]!;
    r.n++;
    return f;
  }

  observe(ev: WorldEvent): void {
    if (ev.type === 'status') return this.status(ev.id, ev.status, ev.stacks);
    if (ev.type !== 'hit') return;
    const w = this.h.world;
    const tgt = w.get(ev.tgtId);
    if (!tgt || tgt.dead) return;
    const prev = this.prevStun.get(tgt.id) ?? 0;
    if (tgt.stunT > prev + 1e-9) {
      // A new stun: immunity and PvP diminishing returns.
      if ((this.immuneUntil.get(tgt.id) ?? -1) > this.now) {
        tgt.stunT = prev;
        return;
      }
      const src = w.get(ev.srcId);
      let f = 1;
      if (src && person(src) && person(tgt) && src !== tgt) f = this.diminish(tgt.id, 'stun', STATUS_RULES.drFactors);
      else if (isBoss(tgt)) {
        const caps = Array.from({ length: STATUS_RULES.bossStunCap + 1 }, (_, i) =>
          i < STATUS_RULES.bossStunCap ? STATUS_RULES.bossStunMul : 0,
        );
        f = this.diminish(tgt.id, 'boss', caps);
      }
      if (f !== 1) tgt.stunT = prev + (tgt.stunT - prev) * f;
      return;
    }
    // A heavy hit wakes a stun that was already running.
    if (prev > 0 && STUN.breaksOnHeavy && ev.damage >= tgt.maxHp * STUN.breaksOnHeavy) tgt.stunT = 0;
  }

  /** A status put on (or refreshed, or gone): poison's burst at max, and PvP diminishing returns for its group. */
  private status(id: number, statusId: string, stacks: number): void {
    const key = `${id}:${statusId}`;
    const before = this.stacks.get(key) ?? 0;
    if (stacks > 0) this.stacks.set(key, stacks);
    else this.stacks.delete(key);
    const def = STATUSES[statusId];
    const w = this.h.world;
    const e = w.get(id);
    if (!def || !e || e.dead || stacks <= 0) return;
    if (def.burstAtMax && stacks >= def.maxStacks && before >= def.maxStacks)
      e.hp = Math.max(1, e.hp - e.maxHp * def.burstAtMax);
    const cat = def.drCategory;
    if (!cat || cat === 'stun' || stacks < before) return;
    const st = e.statuses.find((x) => x.id === statusId);
    const src = st?.srcId !== undefined ? w.get(st.srcId) : undefined;
    if (!st || !src || !person(src) || !person(e) || src === e) return;
    const f = this.diminish(id, cat, STATUS_RULES.drFactors);
    if (f > 0) st.t *= f;
    else {
      e.statuses = e.statuses.filter((x) => x !== st);
      this.stacks.delete(key);
      this.h.emit({ type: 'status', id, status: statusId, stacks: 0 });
    }
  }
}
